import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Formik, Form, Field } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useMutation, gql } from "@apollo/client";
import { z } from "zod";

import { InputField } from "@/components/common/InputField/InputField";
import {
  ChangePasswordMutation,
  ChangePasswordMutationVariables,
} from "@/__generated__/graphql";
import { toast } from "sonner";

const CHANGE_PASSWORD_MUTATION = gql`
  mutation ChangePassword(
    $oldPassword: String!
    $newPassword: String!
    $newPasswordConfirm: String!
    $verificationCode: String
  ) {
    changePassword(
      oldPassword: $oldPassword
      newPassword: $newPassword
      newPasswordConfirm: $newPasswordConfirm
      verificationCode: $verificationCode
    ) {
      success
      message
    }
  }
`;

interface FormValues {
  oldPassword: string;
  newPassword: string;
  newPasswordConfirm: string;
}

const changePasswordSchema = z
  .object({
    oldPassword: z.string().min(1, "Current password is required"),
    newPassword: z.string().min(12, "Password must be at least 8 characters"),
    newPasswordConfirm: z
      .string()
      .min(12, "Password must be at least 8 characters"),
  })
  .refine((data) => data.newPassword === data.newPasswordConfirm, {
    message: "Passwords do not match",
    path: ["newPasswordConfirm"],
  });

const ChangePasswordForm = () => {
  const [showChangePassword, setShowChangePassword] = useState(false);

  const [changePassword] = useMutation<
    ChangePasswordMutation,
    ChangePasswordMutationVariables
  >(CHANGE_PASSWORD_MUTATION);

  return (
    <div className="flex flex-col text-sm gap-1">
      <h5 className="text-lg text-secondary-foreground font-medium">
        Password
      </h5>
      {!showChangePassword ? (
        <div className="flex flex-row justify-between items-center h-[20px]">
          <p className="text-base">Change the password for your account</p>
          <Button
            variant="paint"
            onClick={() => {
              setShowChangePassword(true);
            }}
          >
            Change Password
          </Button>
        </div>
      ) : (
        <div className="mt-2">
          <Formik<FormValues>
            initialValues={{
              oldPassword: "",
              newPassword: "",
              newPasswordConfirm: "",
            }}
            validationSchema={toFormikValidationSchema(changePasswordSchema)}
            onSubmit={async (values, { setSubmitting, setStatus }) => {
              setSubmitting(false);
              try {
                const result = await changePassword({
                  variables: values,
                });
                if (result && result.data && result.data.changePassword) {
                  const { success, message } = result.data.changePassword;
                  if (success) {
                    toast.success("Password changed successfully!");
                    setShowChangePassword(false);
                  } else {
                    setStatus({ formError: message });
                  }
                }
              } catch (err) {
                setStatus({ formError: err });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({ isSubmitting, status }) => (
              <Form className="space-y-4">
                <Field
                  name="oldPassword"
                  type="password"
                  label="Current Password"
                  component={InputField}
                  placeholder="Enter Current password"
                  inputClassName="bg-accent dark:bg-accent"
                  labelClassName="text-[#c3c3c3] text-sm font-medium invert dark:invert-0"
                />
                <Field
                  name="newPassword"
                  type="password"
                  label="New Password"
                  component={InputField}
                  inputClassName="bg-accent dark:bg-accent"
                  placeholder="Enter New password"
                  labelClassName="text-[#c3c3c3]  invert dark:invert-0 text-sm font-medium"
                />
                <Field
                  name="newPasswordConfirm"
                  type="password"
                  inputClassName="bg-accent dark:bg-accent"
                  label="Confirm Password"
                  component={InputField}
                  placeholder="Confirm New password"
                  labelClassName="text-[#c3c3c3] invert dark:invert-0 text-sm font-medium"
                />

                {status?.formError && (
                  <div className="text-[#F04438] text-sm">
                    {status.formError}
                  </div>
                )}

                <div className="flex flex-row items-center gap-2">
                  <Button variant="paint" type="submit" disabled={isSubmitting}>
                    Confirm Password
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setShowChangePassword(false);
                    }}
                    className="text-sm font-medium gap-1 items-center hover:bg-accent !text-primary-foreground"
                  >
                    Cancel
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </div>
  );
};

export default ChangePasswordForm;
